import { ConnectedProps, connect } from 'react-redux';
import React, { FC, useContext, useEffect, useRef, useState } from 'react';
import { ActionButton, Icon, TooltipHost } from '@fluentui/react';
import { DatePicker, Toggle } from '@eta/components';
import { useTranslation } from 'react-i18next';
import { ReduxState } from '../../../common/reducers';
import { UIHelpers } from '../../../helpers/UIHelpers';
import { RoleSelector, RoleType } from './RoleSelector';
import { ClaimLookupDto } from '../../../Services/ClientProxy/clientProxy';
import './Representation.scss';
import { RepresentationContext } from './Context/RepresentationContext';
import { WizardStateAndLogic } from '../../../common/component/WizardPivot';
import { InviteUserMode, inviteUserWizardKey } from './InviteUserMain';
import RepresentationClaims from '../RepresentationClaims';

const mapDispatchToProps = {};

const mapStateToProps = (state: ReduxState) => ({
    currentLanguage: state.reduxlanguage.language,
    claimsCategory: state.referenceData?.taxpayersConfig?.claimsCategory,
    claims: state.referenceData?.taxpayersConfig?.claims,
});

export interface RepresentationProps {
    InviteUserWizard?: WizardStateAndLogic;
    readOnly?: boolean;
    mode?: InviteUserMode;
    taxPayerName?: string;
}

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const Representation: FC<PropsFromRedux & RepresentationProps> = (
    props: PropsFromRedux & RepresentationProps
) => {
    const { t } = useTranslation(['InviteUser']);
    const today: Date = new Date(Date.now());
    const minDate: Date = today;
    const {
        currentLanguage,
        claimsCategory,
        claims,
        InviteUserWizard,
        readOnly,
        mode,
        taxPayerName,
    } = props;
    const { state: currentInvitation, dispatch: dispatchInvitationRequest } =
        useContext(RepresentationContext);
    // eslint-disable-next-line no-nested-ternary
    const invitationRoleType: RoleType = currentInvitation.hasCustomRole
        ? 'custom'
        : currentInvitation.isAdministrator
            ? 'admin'
            : 'user';
    const invitationClaims = claims?.filter((claim) =>
        currentInvitation.claims?.includes(claim.code ?? '')
    );
    const [selectedRoleType, setSelectedRoleType] =
        useState<RoleType>(invitationRoleType);
    const [selectedUserClaims, setselectedUserClaims] =
        useState<ClaimLookupDto[] | undefined>(invitationClaims);
    const [disableFromDate, setDisableFromDate] = useState<boolean>(false);
    const defaultRoleType = useRef<RoleType>(selectedRoleType);
    const getAllClaims = (claimsInput?: ClaimLookupDto[]): string[] => {
        return claimsInput?.map((x) => x.code ?? '') ?? [];
    };

    const resetClaimsToDefault = () => {
        const userClaims = claims?.filter((claim) =>
            // eslint-disable-next-line no-nested-ternary
            defaultRoleType.current === 'admin'
                ? claim.adminRole
                : defaultRoleType.current === 'user'
                ? claim.representativeRole
                : claim.alwaysEnabled
        );
        setselectedUserClaims(userClaims);
        dispatchInvitationRequest({
            type: 'updateRepresentation',
            payload: {
                claims: getAllClaims(userClaims),
            },
        });
    };

    const isValidForm = () => {
        if (
            currentInvitation?.dateActivate === null ||
            currentInvitation?.dateActivate === undefined
        )
            return false;
        if (
            currentInvitation?.isAdministrator === null ||
            currentInvitation?.isAdministrator === undefined
        )
            return false;
        if (!currentInvitation.claims?.length) return false;
        return true;
    };

    useEffect(() => {
        if (mode === 'Edit' && currentInvitation.dateActivate && currentInvitation.dateActivate < new Date()) {
            setDisableFromDate(true);
        }
    }, [currentInvitation]);

    useEffect(() => {
        if (
            InviteUserWizard?.state.selectedTabKey &&
            (mode === 'Edit' ||
                InviteUserWizard?.state.selectedTabKey ===
                inviteUserWizardKey.Branches)
        ) {
            InviteUserWizard.ChangeTabState(
                inviteUserWizardKey.Represent,
                isValidForm() ? 'completed' : 'error'
            );
        }
    }, [InviteUserWizard?.state.selectedTabKey]);

    const onSelectedRoleTypeChange = (selectedRole: RoleType, editPermisssion=false) => {
        setSelectedRoleType(selectedRole);
        switch (selectedRole) {
            case 'custom': {
                let customClaims = claims?.filter((claim) => claim.alwaysEnabled);
                if(editPermisssion) customClaims = invitationClaims;
                else defaultRoleType.current = selectedRole;
                setselectedUserClaims(customClaims);
                dispatchInvitationRequest({
                    type: 'updateRepresentation',
                    payload: {
                        hasCustomRole: true,
                        isAdministrator: false,
                        claims: getAllClaims(customClaims),
                    },
                });
                break;
            }
            case 'admin': {
                const userClaims = claims?.filter((claim) => claim.adminRole);
                setselectedUserClaims(userClaims);
                defaultRoleType.current = selectedRole;
                dispatchInvitationRequest({
                    type: 'updateRepresentation',
                    payload: {
                        hasCustomRole: false,
                        isAdministrator: true,
                        claims: getAllClaims(userClaims),
                    },
                });
                break;
            }
            case 'user': {
                const userClaims = claims?.filter(
                    (claim) => claim.representativeRole
                );
                setselectedUserClaims(userClaims);
                defaultRoleType.current = selectedRole;
                dispatchInvitationRequest({
                    type: 'updateRepresentation',
                    payload: {
                        hasCustomRole: false,
                        isAdministrator: false,
                        claims: getAllClaims(userClaims),
                    },
                });
                break;
            }
            default:
                break;
        }
    };

    useEffect(() => {
        if (mode !== 'Edit' && mode !== 'View') {
            onSelectedRoleTypeChange('admin');
        }
    }, []);

    const onTogglePermissionChange = (
        Claim: ClaimLookupDto,
        checked: boolean
    ) => {
        const selectedClaims = [...(selectedUserClaims ?? [])];
        if (!checked) {
            const index = selectedClaims?.indexOf(Claim);
            // eslint-disable-next-line no-unused-expressions
            if (index !== undefined) selectedClaims?.splice(index, 1);
            // eslint-disable-next-line no-unused-expressions
        } else selectedClaims?.push(Claim);
        setselectedUserClaims(selectedClaims);
        dispatchInvitationRequest({
            type: 'updateRepresentation',
            payload: {
                claims: getAllClaims(selectedClaims),
            },
        });
    };

    return (
        <div id="InvitationRepresentation">
            {readOnly && <div className="taxpayerName">{taxPayerName}</div>}
            <div className="bodyLine">
                <div className="inviteUser_activationDate">
                    <DatePicker
                        label={t('representationFrom')}
                        value={currentInvitation.dateActivate}
                        onSelectDate={(date) => {
                            if (date)
                                dispatchInvitationRequest({
                                    type: 'updateRepresentation',
                                    payload: { dateActivate: date },
                                });
                        }}
                        isRequired
                        minDate={minDate}
                        firstWeekOfYear={1}
                        showMonthPickerAsOverlay
                        allowTextInput
                        formatDate={(date) => {
                            return UIHelpers.FormatDatePickerValue(
                                date,
                                currentLanguage?.langIso4
                            );
                        }}
                        parseDateFromString={UIHelpers.ParseDatePickerString}
                        disabled={readOnly || disableFromDate}
                    />
                </div>

                <div className="inviteUser_activationDate">
                    <DatePicker
                        label={t('representationTo')}
                        value={currentInvitation.dateDeactivate}
                        onSelectDate={(date) => {
                            if (date)
                                dispatchInvitationRequest({
                                    type: 'updateRepresentation',
                                    payload: { dateDeactivate: date },
                                });
                        }}
                        firstWeekOfYear={1}
                        showMonthPickerAsOverlay
                        allowTextInput
                        formatDate={(date) => {
                            return UIHelpers.FormatDatePickerValue(
                                date,
                                currentLanguage?.langIso4
                            );
                        }}
                        parseDateFromString={UIHelpers.ParseDatePickerString}
                        disabled={readOnly}
                    />
                </div>
            </div>
            <RoleSelector
                selection={selectedRoleType}
                className="span2"
                onSelect={(Type) => onSelectedRoleTypeChange(Type ?? 'admin')}
                readOnly={readOnly}
            />
            <br />
            {selectedRoleType === 'custom' && mode !== 'View' ? (
                <div className="editablePermissionRoles">
                    {claimsCategory?.map((claimCategory) => {
                        const userClaims = claims?.filter(
                            (claim) =>
                                claim?.parentCode &&
                                Number(claim.parentCode) === claimCategory.id
                        );
                        const userClaimslength = userClaims?.length ?? 0;
                        return  userClaimslength ? (
                            <>
                                {userClaims?.map((userClaim) => {
                                        return (
                                            <div className="toggleLine">
                                                <Toggle
                                                    label={(
                                                        <>
                                                            <span className="bold">
                                                                {
                                                                    claimCategory.name
                                                                }
                                                            </span>
                                                            {' - '}
                                                            <span className="toggleLabel">
                                                                {userClaim.name}
                                                                {userClaim.alwaysEnabled
                                                                    ? ` (${t(
                                                                        'alwaysEnabled'
                                                                    )})`
                                                                    : ''}
                                                                {userClaim.restrictedBranch
                                                                    ? '*'
                                                                    : ''}
                                                            </span>
                                                        </>
                                                    )}
                                                    inlineLabel
                                                    checked={
                                                        selectedUserClaims?.find(
                                                            (x) =>
                                                                x.id ===
                                                                userClaim.id
                                                        ) !== undefined
                                                    }
                                                    disabled={
                                                        userClaim.alwaysEnabled ||
                                                        readOnly
                                                    }
                                                    onChange={(_, checked) =>
                                                        onTogglePermissionChange(
                                                            userClaim,
                                                            checked ?? false
                                                        )
                                                    }
                                                />
                                                <TooltipHost
                                                    content={
                                                        userClaim.description
                                                    }
                                                >
                                                    <Icon
                                                        iconName="Info"
                                                        aria-label="Info tooltip"
                                                    />
                                                </TooltipHost>
                                            </div>
                                        );
                                    })}
                            </>
                        ) : (
                                <></>
                            );
                    })}
                    {!readOnly && (
                        <div className="resetLink">
                            <ActionButton
                                onClick={() => resetClaimsToDefault()}
                            >
                                {t('reset_Default')}
                            </ActionButton>
                        </div>
                    )}
                    <p className="asrtic_Permission">
                        {t('asrtic_Permission_Info')}
                    </p>
                </div>
            ) : (
                    <div className="permissionRoles">
                        {!readOnly && (
                            <ActionButton
                                iconProps={{ iconName: 'Edit' }}
                                onClick={() => onSelectedRoleTypeChange('custom', true)}
                            >
                                {t('editPermissions')}
                            </ActionButton>
                        )}
                        <br />
                        <RepresentationClaims Claims={selectedUserClaims} />
                        <p className="asrtic_Permission">
                            {t('asrtic_Permission_Info')}
                        </p>
                    </div>
                )}
        </div>
    );
};

export default connector(Representation);
